import React, { useState, useEffect } from 'react';
import { Shield, Key, Clock, AlertTriangle, Download, Trash2, Eye, EyeOff } from 'lucide-react';
import { secureApi, tokenStorage } from '../utils/auth';
import SecurityAlert from '../components/SecurityAlert';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const AccountSecurity = () => {
  const [activity, setActivity] = useState([]);
  const [securityCheck, setSecurityCheck] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const user = tokenStorage.getUser();

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    try {
      // Load activity log
      const activityData = await secureApi.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/account/activity?limit=10`
      );
      setActivity(activityData.activity || []);

      // Check for suspicious activity
      const checkData = await secureApi.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/account/security-check`
      );
      setSecurityCheck(checkData);
    } catch (error) {
      console.error('Failed to load security data:', error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (passwords.new !== passwords.confirm) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    try {
      await secureApi.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`,
        {
          current_password: passwords.current,
          new_password: passwords.new
        }
      );

      setMessage({ type: 'success', text: 'Password changed successfully. Please log in again.' });
      
      // Clear tokens and redirect to login after 2 seconds
      setTimeout(() => {
        tokenStorage.clearTokens();
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to change password' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const data = await secureApi.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/account/export-data`
      );
      
      // Download as JSON
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `standari-data-export-${new Date().toISOString()}.json`;
      a.click();
      
      setMessage({ type: 'success', text: 'Data exported successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to export data' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmation = prompt('Type "DELETE_MY_ACCOUNT" to confirm account deletion:');
    
    if (confirmation !== 'DELETE_MY_ACCOUNT') {
      setMessage({ type: 'error', text: 'Account deletion cancelled' });
      return;
    }

    try {
      setLoading(true);
      await secureApi.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/account`,
        { confirm: 'DELETE_MY_ACCOUNT' }
      );
      
      tokenStorage.clearTokens();
      window.location.href = '/';
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete account' });
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Account Security</h1>
          </div>
          <p className="text-gray-600">Manage your security settings and view account activity</p>
        </div>

        {/* Message Alert */}
        {message && (
          <SecurityAlert
            type={message.type}
            message={message.text}
            onDismiss={() => setMessage(null)}
          />
        )}

        {/* Security Check */}
        {securityCheck && securityCheck.is_suspicious && (
          <SecurityAlert
            type="warning"
            title="Suspicious Activity Detected"
            message={`We detected ${securityCheck.failed_logins} failed login attempts and ${securityCheck.unauthorized_attempts} unauthorized access attempts in the last hour. Please review your account activity.`}
          />
        )}

        {/* Change Password */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <button
            onClick={() => setShowChangePassword(!showChangePassword)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center gap-3">
              <Key className="h-6 w-6 text-gray-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
                <p className="text-sm text-gray-600">Update your password regularly for security</p>
              </div>
            </div>
            <span className="text-gray-400">{showChangePassword ? '▲' : '▼'}</span>
          </button>

          {showChangePassword && (
            <form onSubmit={handleChangePassword} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwords.current}
                    onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.current ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwords.new}
                    onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <PasswordStrengthMeter password={passwords.new} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Change Password'}
              </button>
            </form>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="h-6 w-6 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          </div>
          
          <div className="space-y-3">
            {activity.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            ) : (
              activity.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.description || item.action}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {formatDate(item.timestamp)} {item.ip_address && `• ${item.ip_address}`}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Data Export & Account Deletion */}
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Data & Privacy</h2>
            
            <button
              onClick={handleExportData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 mb-3"
            >
              <Download className="h-5 w-5" />
              <span>Export My Data</span>
            </button>
            <p className="text-sm text-gray-600">Download all your data in JSON format (GDPR compliance)</p>
          </div>

          <hr className="my-4" />

          <div>
            <button
              onClick={() => setShowDeleteAccount(!showDeleteAccount)}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
            >
              <Trash2 className="h-5 w-5" />
              <span>Delete Account</span>
            </button>
            
            {showDeleteAccount && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <SecurityAlert
                  type="error"
                  title="Warning: This action cannot be undone"
                  message="Deleting your account will permanently remove all your data, documents, and settings. This action cannot be reversed."
                />
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading}
                  className="mt-4 w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Deleting...' : 'I Understand, Delete My Account'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSecurity;
